import {
  fork,
  put,
  all,
  call,
  take,
  cancel,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import {
  getFirestore,
  collection,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import rsf from '../../helpers/firebase';
import { toDateFirebase } from '../../helpers/sharedFunction';
import toastr from 'toastr';
import types from './actionTypes';
import * as actions from './actions';

const db = getFirestore(rsf.app);

const normalizeDesign = (design) => {
  if (!design) return design;
  if (typeof design !== 'string') return design;

  try {
    return JSON.parse(design);
  } catch (error) {
    return design;
  }
};

const landingTemplateTransformer = (payload) => {
  const landingTemplates = [];
  payload.forEach((doc) => {
    const data = doc.data();

    landingTemplates.push({
      id: doc.id,
      ...data,
      design: normalizeDesign(data.design),
      createdAt: toDateFirebase(doc, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(doc, data, 'updatedAt').toDate(),
      }),
    });
  });
  return landingTemplates;
};

function* syncLandingTemplatesSaga() {
  const countryId = yield select((state) => state.Dashboard.countryId);

  const landingTemplatesRef = query(
    collection(db, 'landingTemplates'),
    where('countryId', '==', countryId),
    orderBy('createdAt', 'desc'),
  );

  const task = yield fork(rsf.firestore.syncCollection, landingTemplatesRef, {
    successActionCreator: actions.syncLandingTemplatesSuccess,
    failureActionCreator: actions.syncLandingTemplatesFailure,
    transform: (payload) => landingTemplateTransformer(payload),
  });

  yield take(types.RESET_LANDING_TEMPLATE_STATE);
  yield cancel(task);
}

function* saveLandingTemplateSaga({ template }) {
  try {
    const countryId = yield select((state) => state.Dashboard.countryId);
    const { id, design, name, description } = template;
    const isUpdate = Boolean(id);

    const designStr = design && typeof design === 'object' ? JSON.stringify(design) : design;

    const basePayload = {
      name,
      description: description || '',
      countryId,
      design: designStr,
      updatedAt: serverTimestamp(),
    };

    if (isUpdate) {
      const docRef = doc(db, 'landingTemplates', id);
      yield call(rsf.firestore.setDocument, docRef, basePayload, { merge: true });
    } else {
      const templatesRef = collection(db, 'landingTemplates');
      yield call(rsf.firestore.addDocument, templatesRef, {
        ...basePayload,
        archived: false,
        createdAt: serverTimestamp(),
      });
    }

    yield put(actions.saveLandingTemplateSuccess());
    toastr.success('Template saved!', '');
  } catch (error) {
    yield put(actions.saveLandingTemplateFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* archiveLandingTemplateSaga({ templateId }) {
  try {
    const docRef = doc(db, 'landingTemplates', templateId);

    yield call(
      rsf.firestore.setDocument,
      docRef,
      { archived: true },
      { merge: true },
    );

    yield put(actions.archiveLandingTemplateSuccess());
    toastr.success('Template archived!', '');
  } catch (error) {
    yield put(actions.archiveLandingTemplateFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* landingTemplateSaga() {
  yield all([
    takeLatest(types.SYNC_LANDING_TEMPLATES.REQUEST, syncLandingTemplatesSaga),
    takeLeading(types.SAVE_LANDING_TEMPLATE.REQUEST, saveLandingTemplateSaga),
    takeLeading(
      types.ARCHIVE_LANDING_TEMPLATE.REQUEST,
      archiveLandingTemplateSaga,
    ),
  ]);
}

export default landingTemplateSaga;
